import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BedDouble, Bath, Maximize, Car, Calendar, MapPin, ArrowLeft, Heart, Share2,
  Check, Phone, Mail, MessageCircle, Cuboid, Play, ChevronLeft, ChevronRight, Loader2,
} from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { PropertyCard } from "../components/PropertyCard";
import { formatPrice, typeLabel, statusColor, img } from "../lib/utils-app";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

export default function PropertyDetail() {
  const { id } = useParams();
  const { t, lang, L } = useLang();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [active, setActive] = useState(0);
  const [fav, setFav] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null);
    api.get(`/properties/${id}`).then((r) => { setP(r.data); setActive(0); });
    api.get(`/properties/${id}/similar`).then((r) => setSimilar(r.data.items));
  }, [id]);

  const toggleFav = async () => {
    if (!user) { toast.error(lang === "es" ? "Inicia sesión para guardar" : "Sign in to save"); return; }
    try {
      if (fav) { await api.delete(`/favorites/${id}`); setFav(false); }
      else { await api.post(`/favorites/${id}`); setFav(true); toast.success(t("common.saved")); }
    } catch {}
  };

  if (!p) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  const isRent = p.transaction_type === "rent";
  const images = p.images || [];
  const mortgage = Math.round((p.price * 0.8 * 0.006) + (isRent ? 0 : 0));
  const monthly = isRent ? p.price : mortgage;

  return (
    <div className="pt-24">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6">
        <Link to={isRent ? "/rent" : "/buy"} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6" data-testid="back-link">
          <ArrowLeft size={16} /> {t("common.backToListings")}
        </Link>

        {/* Gallery */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 mb-8" data-testid="gallery">
          <div className="lg:col-span-3 relative aspect-[16/10] overflow-hidden bg-card">
            <motion.img key={active} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              src={img(images[active])} alt={L(p.title)} className="w-full h-full object-cover" />
            <div className="absolute top-4 left-4 flex gap-2">
              <span className="label-caps px-3 py-1.5 bg-primary text-primary-foreground">{isRent ? t("common.forRent") : t("common.forSale")}</span>
              <span className={`text-xs px-3 py-1.5 border capitalize ${statusColor(p.status)}`}>{p.status}</span>
            </div>
            <Link to={`/virtual-tour/${p.id}`} className="absolute bottom-4 left-4 z-10 flex items-center gap-2 px-4 py-2.5 bg-brandgreen/95 backdrop-blur text-white text-xs tracking-widest uppercase hover:bg-primary transition-colors duration-300 shadow-lg" data-testid="start-virtual-tour">
              <Cuboid size={15} /> {lang === "es" ? "Iniciar Tour Virtual" : "Start Virtual Tour"} <span className="opacity-80">360°</span>
            </Link>
            {images.length > 1 && (
              <>
                <button onClick={() => setActive((a) => (a - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center bg-black/40 backdrop-blur text-white hover:bg-primary transition-colors" data-testid="gallery-prev"><ChevronLeft /></button>
                <button onClick={() => setActive((a) => (a + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center bg-black/40 backdrop-blur text-white hover:bg-primary transition-colors" data-testid="gallery-next"><ChevronRight /></button>
                <div className="absolute bottom-3 right-3 text-xs bg-black/50 backdrop-blur text-white px-2.5 py-1">{active + 1} / {images.length}</div>
              </>
            )}
          </div>
          <div className="lg:col-span-1 grid grid-cols-4 lg:grid-cols-1 gap-2">
            {(images.length ? images : [""]).slice(0, 4).map((im, i) => (
              <button key={i} onClick={() => setActive(i)} className={`aspect-square lg:aspect-[16/10] overflow-hidden hover-zoom border-2 transition-colors ${active === i ? "border-primary" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                <img src={img(im)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Split content */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="label-caps text-primary mb-2">{typeLabel(p.property_type, lang)}</p>
                <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight">{L(p.title)}</h1>
                <p className="flex items-center gap-2 text-muted-foreground mt-3"><MapPin size={16} className="text-primary" /> {p.address}, {p.neighborhood}, {p.city} {p.zip}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={toggleFav} className="w-11 h-11 grid place-items-center border border-border hover:border-primary transition-colors" data-testid="detail-favorite"><Heart size={18} className={fav ? "fill-primary text-primary" : ""} /></button>
                <button onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }} className="w-11 h-11 grid place-items-center border border-border hover:border-primary transition-colors" data-testid="share-btn"><Share2 size={18} /></button>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-6 py-6 border-y border-border">
              {p.bedrooms > 0 && <Stat icon={BedDouble} v={p.bedrooms} l={t("common.bed")} />}
              {p.bathrooms > 0 && <Stat icon={Bath} v={p.bathrooms} l={t("common.bath")} />}
              {p.sqft > 0 && <Stat icon={Maximize} v={p.sqft.toLocaleString()} l={t("common.sqft")} />}
              {p.parking > 0 && <Stat icon={Car} v={p.parking} l={t("detail.parking")} />}
            </div>

            <section className="py-8">
              <h2 className="font-serif text-3xl mb-4">{t("detail.overview")}</h2>
              <p className="text-muted-foreground font-light leading-relaxed text-lg">{L(p.description)}</p>
            </section>

            {/* Details grid */}
            <section className="py-4">
              <h2 className="font-serif text-3xl mb-5">{t("detail.details")}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border border border-border">
                <DetailItem l={t("detail.type")} v={typeLabel(p.property_type, lang)} />
                {p.year_built && <DetailItem l={t("detail.built")} v={p.year_built} />}
                {p.lot_size > 0 && <DetailItem l={t("detail.lot")} v={`${p.lot_size.toLocaleString()} sqft`} />}
                <DetailItem l={t("detail.status")} v={p.status} />
                <DetailItem l="ID" v={p.id.slice(0, 8)} />
                <DetailItem l={t("common.price")} v={formatPrice(p.price, p.currency)} />
              </div>
            </section>

            {/* Features */}
            {p.amenities?.length > 0 && (
              <section className="py-8">
                <h2 className="font-serif text-3xl mb-5">{t("detail.features")}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[...(p.amenities || []), ...(p.features || [])].map((a) => (
                    <div key={a} className="flex items-center gap-2.5 text-sm font-light">
                      <span className="w-5 h-5 grid place-items-center bg-primary/15 text-primary"><Check size={13} /></span>{a}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Virtual tour + Map */}
            <section className="py-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {p.virtual_tour_url && (
                <Link to={`/virtual-tour/${p.id}`} className="group relative aspect-video overflow-hidden bg-card border border-border" data-testid="virtual-tour">
                  <img src={img(images[1] || images[0])} alt="" className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity" />
                  <div className="absolute inset-0 grid place-items-center">
                    <div className="text-center">
                      <span className="w-14 h-14 grid place-items-center rounded-full bg-primary text-primary-foreground mx-auto mb-2"><Cuboid size={24} /></span>
                      <p className="label-caps text-white">{t("detail.virtualTour")}</p>
                    </div>
                  </div>
                </Link>
              )}
              <div className="aspect-video bg-card border border-border relative overflow-hidden" data-testid="location-map">
                <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "40px 40px", opacity: 0.4 }} />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                  <MapPin size={32} className="text-primary mx-auto animate-bounce" />
                  <p className="label-caps mt-2">{p.neighborhood}</p>
                </div>
                <p className="absolute bottom-3 left-3 label-caps text-muted-foreground">{t("detail.location")}</p>
              </div>
            </section>
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:sticky lg:top-24 self-start space-y-4">
            <div className="border border-border p-6 bg-card">
              <p className="font-serif text-4xl text-primary">{formatPrice(p.price, p.currency)}{isRent && <span className="text-base text-muted-foreground font-sans">{t("common.perMonth")}</span>}</p>
              <div className="mt-4 pt-4 border-t border-border flex justify-between text-sm">
                <span className="text-muted-foreground">{isRent ? t("detail.relatedCosts") : t("detail.mortgage")}</span>
                <span className="font-medium">{formatPrice(monthly, p.currency)}{t("common.perMonth")}</span>
              </div>
              <div className="grid grid-cols-1 gap-2 mt-5">
                <TourDialog property={p} />
                <LeadDialog property={p} />
              </div>
            </div>

            {p.agent && (
              <div className="border border-border p-6 bg-card">
                <p className="label-caps text-muted-foreground mb-4">{t("detail.agent")}</p>
                <div className="flex items-center gap-3 mb-4">
                  <img src={p.agent.photo} alt={p.agent.name} className="w-14 h-14 rounded-full object-cover" />
                  <div>
                    <p className="font-serif text-xl">{p.agent.name}</p>
                    <p className="text-xs text-muted-foreground">{p.agent.title}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <a href={`tel:${p.agent.phone}`} className="flex items-center justify-center gap-1.5 py-2.5 border border-border hover:border-primary transition-colors text-xs" data-testid="agent-call"><Phone size={14} /></a>
                  <a href={`mailto:${p.agent.email}`} className="flex items-center justify-center gap-1.5 py-2.5 border border-border hover:border-primary transition-colors text-xs" data-testid="agent-email"><Mail size={14} /></a>
                  <a href={`https://wa.me/${p.agent.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 py-2.5 border border-border hover:border-emerald-500 transition-colors text-xs text-emerald-400" data-testid="agent-whatsapp"><MessageCircle size={14} /></a>
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* Similar */}
        {similar.length > 0 && (
          <section className="py-16 border-t border-border mt-12">
            <h2 className="font-serif text-4xl font-light mb-8">{t("common.similar")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {similar.map((s, i) => <PropertyCard key={s.id} property={s} index={i} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, v, l }) => (
  <div className="flex items-center gap-2.5">
    <Icon size={22} className="text-primary" strokeWidth={1.3} />
    <div><span className="font-serif text-2xl">{v}</span> <span className="text-sm text-muted-foreground">{l}</span></div>
  </div>
);

const DetailItem = ({ l, v }) => (
  <div className="bg-card p-4">
    <p className="label-caps text-muted-foreground text-[0.6rem]">{l}</p>
    <p className="mt-1 capitalize font-light">{v}</p>
  </div>
);

function TourDialog({ property }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "10:00" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/appointments", { ...form, property_id: property.id, agent_id: property.agent_id, type: "tour" });
      toast.success(lang === "es" ? "¡Visita solicitada! Revisa tu correo." : "Tour requested! Check your email.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", date: "", time: "10:00" });
    } catch { toast.error("Error"); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="w-full py-3 bg-primary text-primary-foreground label-caps hover:brightness-110 transition-[filter] flex items-center justify-center gap-2" data-testid="schedule-tour-btn"><Calendar size={15} /> {t("common.scheduleTour")}</button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{t("common.scheduleTour")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label={t("common.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="tour-name" required />
          <Field label={t("common.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="tour-email" required />
          <Field label={t("common.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="tour-phone" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="tour-date" required />
            <Field label="Time" type="time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} testid="tour-time" required />
          </div>
          <button disabled={loading} className="w-full py-3 bg-primary text-primary-foreground label-caps disabled:opacity-50" data-testid="tour-submit">{loading ? t("common.sending") : t("common.submit")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LeadDialog({ property }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, property_id: property.id, source: "website", type: "inquiry" });
      toast.success(lang === "es" ? "¡Solicitud enviada!" : "Request sent!");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Error"); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="w-full py-3 border border-primary text-primary label-caps hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="request-info-btn">{t("common.requestInfo")}</button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{t("common.requestInfo")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label={t("common.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="lead-name" required />
          <Field label={t("common.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="lead-email" required />
          <Field label={t("common.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="lead-phone" />
          <div>
            <label className="label-caps text-muted-foreground">{t("common.message")}</label>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} data-testid="lead-message"
              className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors resize-none" />
          </div>
          <button disabled={loading} className="w-full py-3 bg-primary text-primary-foreground label-caps disabled:opacity-50" data-testid="lead-submit">{loading ? t("common.sending") : t("common.submit")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="label-caps text-muted-foreground">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid}
        className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors" />
    </div>
  );
}
